import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { watchesApi, Watch } from '../watchesApi';
import { seedPatchFor } from '../watchSpecs';
import { errorMessage as errText } from '../errors';
import { watchImages } from '../watchImages';
import { scrapeInto } from '../scrapeApi';
import BRANDS from '../data/brands.json';

const BRAND_LIST = BRANDS as string[];

const emptyDraft = { brand: '', model: '', reference: '', source_url: '', notes: '' };
type Draft = typeof emptyDraft;

/** Tiles are the default; the choice is remembered between visits. */
type View = 'tile' | 'list';
const VIEW_KEY = 'watchwatch:watchView';

const storedView = (): View => {
  try {
    return window.localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'tile';
  } catch {
    // Private-mode browsers can throw on localStorage access; the default is
    // a fine answer and not worth failing the page over.
    return 'tile';
  }
};



/**
 * Display order: brand, then model.
 *
 * localeCompare with sensitivity 'base' rather than a plain `<` comparison,
 * so case and accents do not split the list -- "a. lange" files with
 * "A. Lange & Söhne" instead of landing after Zenith, which is what a raw
 * code-point sort would do.
 *
 * Sorted here on render as well as in the query: an added or renamed watch
 * has to land in its right place immediately, not only after a reload.
 */
const byBrandThenModel = (a: Watch, b: Watch) =>
  a.brand.localeCompare(b.brand, undefined, { sensitivity: 'base' }) ||
  a.model.localeCompare(b.model, undefined, { sensitivity: 'base' });

export default function MyWatches() {
  const [watches, setWatches] = useState<Watch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [adding, setAdding] = useState(false);
  // Outcome of the scrape a source URL on the add form kicks off. Shown above
  // the list, since the form itself has closed by the time it resolves.
  const [scrapeNote, setScrapeNote] = useState('');
  // The add form starts collapsed so the list itself is what the page leads
  // with -- adding a watch is occasional, reading the list is not.
  const [formOpen, setFormOpen] = useState(false);
  const brandInputRef = useRef<HTMLInputElement>(null);

  const [view, setView] = useState<View>(storedView);

  // Signed URLs for every photo on the page, keyed by storage path. The
  // bucket is private, so paths on the row are not renderable as-is.
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    try {
      setWatches(await watchesApi.list());
      setError('');
    } catch (err) {
      setError(errText(err, 'Could not load your watch list.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_KEY, view);
    } catch {
      // Not being able to remember the choice is not worth surfacing.
    }
  }, [view]);

  // One batched signing request per list load, rather than one per watch.
  useEffect(() => {
    const paths = watches.map(w => w.image_url).filter((p): p is string => !!p);
    if (paths.length === 0) {
      setImageUrls({});
      return;
    }
    let cancelled = false;
    watchImages.signedUrls(paths).then(map => {
      if (!cancelled) setImageUrls(map);
    });
    return () => {
      cancelled = true;
    };
  }, [watches]);

  // Opening the panel puts the cursor in the first field, so the path is
  // click-then-type rather than click-then-tab.
  useEffect(() => {
    if (formOpen) brandInputRef.current?.focus();
  }, [formOpen]);

  function closeForm() {
    setFormOpen(false);
    setDraft(emptyDraft);
  }

  const canAdd = !!draft.brand.trim() && !!draft.model.trim() && !adding;

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!canAdd) return;
    setAdding(true);
    try {
      const created = await watchesApi.add(draft);

      // Fill specs from the bundled reference data if the model is known.
      // Deliberately not awaited into the failure path: a spec lookup that
      // fails must not make a successful add look like it failed, so the
      // watch goes into the list either way.
      const seeded = await applySeed(created);
      setWatches(prev => [seeded, ...prev]);
      setDraft(emptyDraft);
      setFormOpen(false);
      setError('');

      // A URL on the add form means what it means on the detail page: read
      // that page now. Deliberately after the row is in the list, so a slow or
      // broken site never delays or undoes the add itself.
      if (seeded.source_url) await applyScrapeToNew(seeded);
    } catch (err) {
      setError(errText(err, 'Could not add that watch.'));
    } finally {
      setAdding(false);
    }
  }

  /**
   * Reads the new watch's source page and folds the result back into the list.
   *
   * Failures land in `scrapeNote`, not `error`: the watch was added, and a
   * page that would not load must not present itself as the add having failed.
   */
  async function applyScrapeToNew(w: Watch) {
    setScrapeNote(`Reading ${w.source_url}…`);
    try {
      const outcome = await scrapeInto(w);
      setWatches(prev => prev.map(x => (x.id === outcome.watch.id ? outcome.watch : x)));
      setScrapeNote([outcome.note, ...outcome.warnings].join(' '));
    } catch (err) {
      setScrapeNote(errText(err, 'Could not read that page.'));
    }
  }

  /**
   * Writes bundled specs into a watch that has empty ones. Returns the watch
   * unchanged if there is nothing to fill or the write fails -- specs are a
   * bonus, never a reason to lose the row the user just created.
   */
  async function applySeed(w: Watch): Promise<Watch> {
    const patch = seedPatchFor(w);
    if (!patch) return w;
    try {
      return await watchesApi.update(w.id, patch);
    } catch {
      return w;
    }
  }

  const sorted = [...watches].sort(byBrandThenModel);

  return (
    <>
      <div className="page-header">
        <h1>My Watches</h1>
      </div>

      {/* Outside the form so it survives the panel being collapsed. */}
      <datalist id="brand-options">
        {BRAND_LIST.map(b => <option key={b} value={b} />)}
      </datalist>

      <div className="card add-watch">
        <button
          type="button"
          className="add-watch-toggle"
          aria-expanded={formOpen}
          aria-controls="add-watch-form"
          onClick={() => (formOpen ? closeForm() : setFormOpen(true))}
        >
          <span>Add a watch</span>
          <span className="add-watch-icon" aria-hidden="true">
            {formOpen ? '−' : '+'}
          </span>
        </button>

        {formOpen && (
          <form id="add-watch-form" className="watch-form" onSubmit={handleAdd}>
            <div className="watch-form-row">
              <div className="watch-field">
                <label className="auth-label" htmlFor="brand">Brand</label>
                {/* datalist gives autocomplete against the canonical list while
                    still accepting anything typed -- microbrands and vintage
                    marques are not blocked by an incomplete list. */}
                <input
                  id="brand"
                  ref={brandInputRef}
                  list="brand-options"
                  className="auth-input"
                  placeholder="e.g. Tudor"
                  value={draft.brand}
                  onChange={e => setDraft({ ...draft, brand: e.target.value })}
                />
              </div>

              <div className="watch-field">
                <label className="auth-label" htmlFor="model">Model</label>
                <input
                  id="model"
                  className="auth-input"
                  placeholder="e.g. Black Bay 58"
                  value={draft.model}
                  onChange={e => setDraft({ ...draft, model: e.target.value })}
                />
              </div>

              <div className="watch-field">
                <label className="auth-label" htmlFor="reference">
                  Reference <span className="optional">optional</span>
                </label>
                <input
                  id="reference"
                  className="auth-input"
                  placeholder="e.g. M79030N"
                  value={draft.reference}
                  onChange={e => setDraft({ ...draft, reference: e.target.value })}
                />
              </div>
            </div>

            <label className="auth-label" htmlFor="source-url">
              Source page <span className="optional">optional</span>
            </label>
            <input
              id="source-url"
              className="auth-input"
              type="url"
              inputMode="url"
              placeholder="https://… a brand or retailer page for this model"
              value={draft.source_url}
              onChange={e => setDraft({ ...draft, source_url: e.target.value })}
            />
            <p className="field-hint">
              If you add one, the page is read and used to fill in any specs and
              photo you don't already have.
            </p>

            <label className="auth-label mt-2" htmlFor="notes">
              Notes <span className="optional">optional</span>
            </label>
            <input
              id="notes"
              className="auth-input"
              placeholder="Anything you want to remember about it"
              value={draft.notes}
              onChange={e => setDraft({ ...draft, notes: e.target.value })}
            />

            <div className="flex gap-1 mt-2">
              <button type="submit" className="btn btn-primary" disabled={!canAdd}>
                {adding ? 'Adding…' : 'Add watch'}
              </button>
              <button type="button" className="btn btn-outline" onClick={closeForm}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {error && <p className="auth-error">{error}</p>}
      {scrapeNote && <p className="detail-hint">{scrapeNote}</p>}

      {loading ? (
        <p className="mt-3">Loading…</p>
      ) : watches.length === 0 ? (
        <div className="empty-state">
          <h2>Your watch list is empty</h2>
          <p>Add one and Watch News can filter down to just what you follow.</p>
          <button className="btn btn-primary mt-2" onClick={() => setFormOpen(true)}>
            Add your first watch
          </button>
        </div>
      ) : (
        <>
          <div className="view-toggle">
            <button
              className={`chip${view === 'tile' ? ' chip-active' : ''}`}
              onClick={() => setView('tile')}
              aria-pressed={view === 'tile'}
            >
              Tiles
            </button>
            <button
              className={`chip${view === 'list' ? ' chip-active' : ''}`}
              onClick={() => setView('list')}
              aria-pressed={view === 'list'}
            >
              List
            </button>
          </div>

          {view === 'tile' ? (
            <div className="watch-tiles">
              {sorted.map(w => {
                const src = w.image_url ? imageUrls[w.image_url] : undefined;
                return (
                  <Link key={w.id} className="watch-tile" to={`/watches/${w.id}`}>
                    {/* No photo means no image slot: the tile is simply
                        shorter. Revisit once adoption is clearer. */}
                    {src && (
                      <span className="watch-tile-img">
                        <img src={src} alt="" loading="lazy" />
                      </span>
                    )}
                    <span className="watch-tile-body">
                      <span className="brand">{w.brand}</span>
                      <span className="watch-tile-model">
                        {w.model}
                        {w.reference && <span className="tag watch-ref">{w.reference}</span>}
                      </span>
                      {w.notes && <span className="watch-notes">{w.notes}</span>}
                    </span>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="watch-list">
              {sorted.map(w => {
                const src = w.image_url ? imageUrls[w.image_url] : undefined;
                return (
                  <Link key={w.id} className="watch-row" to={`/watches/${w.id}`}>
                    {src && <img className="watch-row-thumb" src={src} alt="" loading="lazy" />}
                    <span className="watch-row-text">
                      <span className="brand">{w.brand}</span>
                      <span className="watch-row-model">
                        {w.model}
                        {w.reference && <span className="tag watch-ref">{w.reference}</span>}
                      </span>
                      {w.notes && <span className="watch-notes">{w.notes}</span>}
                    </span>
                  </Link>
                );
              })}
            </div>
          )}
        </>
      )}
    </>
  );
}
